import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import {
  Search, Download, Filter, ChevronDown, ChevronUp, CheckCircle,
  XCircle, Eye, Mail, FileText, Shield, ChevronLeft, ChevronRight,
} from 'lucide-react';
import type { ClaimResult } from '@/types/api.types';
import { OverlapFlagsCell } from '@/components/claims/OverlapFlagsCell';
import { DeficiencyPanel } from '@/components/claims/DeficiencyPanel';
import { QualificationChecklist } from '@/components/claims/QualificationChecklist';

const mockResults: ClaimResult[] = [
  { id: '1', claimsiqId: 'CIQ-2025-00147', email: 'jsmith@email.com', currentAddress: '123 Main St', city: 'Houston', state: 'TX', zip: '77001', moveInDate: 'Jan 2019', currentRent: '$1,850', occupation: 'Firefighter', militaryService: 'No', claimType: 'RealPage', qualification: 'Qualified', defendant: 'Greystar Management', status: 'New', lastUpdated: '2025-01-26', overlapFlags: [{ tortCode: 'RLP', matchType: 'geographic', confidenceScore: 'confirmed', ruleApplied: 'RULE-001', evidenceOnFile: ['Lease agreement', 'Address verified via Smarty DPV-Y'], evidenceGaps: [] }, { tortCode: 'PFAS', matchType: 'geographic', confidenceScore: 'probable', ruleApplied: 'RULE-007', evidenceOnFile: ['Address near contamination site'], evidenceGaps: ['Water test results'] }], deficiencies: [{ deficiencyCode: 'DEF_OVERLAP_EVIDENCE_GAP', message: 'The overlap analysis identified evidence gaps that must be resolved before the claim can be substantiated.', severity: 'WARNING', raisedAt: '2025-01-26' }, { deficiencyCode: 'DEF_MISSING_REQUIRED_DOC', message: 'One or more required documents have not been uploaded or accepted for this claimant.', severity: 'CRITICAL', raisedAt: '2025-01-25' }] },
  { id: '2', claimsiqId: 'CIQ-2025-00148', email: 'jdoe@email.com', currentAddress: '456 Oak Ave', city: 'Atlanta', state: 'GA', zip: '30309', moveInDate: 'Mar 2020', currentRent: '$2,100', occupation: 'Military', militaryService: 'Yes', claimType: 'RealPage', qualification: 'Qualified', defendant: 'Lincoln Property', status: 'Contacted', lastUpdated: '2025-01-25', overlapFlags: [{ tortCode: 'RLP', matchType: 'geographic', confidenceScore: 'confirmed', ruleApplied: 'RULE-001', evidenceOnFile: ['Lease agreement'], evidenceGaps: [] }, { tortCode: 'PFAS', matchType: 'geographic', confidenceScore: 'confirmed', ruleApplied: 'RULE-007', evidenceOnFile: ['Military service record', 'Base near contamination'], evidenceGaps: [] }, { tortCode: 'SMA', matchType: 'behavioral', confidenceScore: 'unverified', ruleApplied: 'TRIG-003', evidenceOnFile: [], evidenceGaps: ['Platform usage evidence'] }], deficiencies: [{ deficiencyCode: 'DEF_OVERLAP_EVIDENCE_GAP', message: 'The overlap analysis identified evidence gaps that must be resolved before the claim can be substantiated.', severity: 'WARNING', raisedAt: '2025-01-25' }] },
  { id: '3', claimsiqId: 'CIQ-2025-00149', email: 'mbrown@email.com', currentAddress: '789 Pine Rd', city: 'Dallas', state: 'TX', zip: '75201', moveInDate: 'Sep 2018', currentRent: '$1,650', occupation: 'Teacher', militaryService: 'No', claimType: 'RealPage', qualification: 'Qualified', defendant: 'Camden Property', status: 'New', lastUpdated: '2025-01-26', overlapFlags: [{ tortCode: 'RLP', matchType: 'geographic', confidenceScore: 'confirmed', ruleApplied: 'RULE-001', evidenceOnFile: ['Lease agreement', 'Rent history'], evidenceGaps: [] }, { tortCode: 'VGA', matchType: 'behavioral', confidenceScore: 'probable', ruleApplied: 'TRIG-002', evidenceOnFile: ['Gaming hours > 2/day'], evidenceGaps: ['Gamer tag verification'] }], deficiencies: [{ deficiencyCode: 'DEF_MISSING_REQUIRED_DOC', message: 'One or more required documents have not been uploaded or accepted for this claimant.', severity: 'CRITICAL', raisedAt: '2025-01-26' }, { deficiencyCode: 'DEF_OVERLAP_PENDING', message: 'A probable overlap has been pending review for more than 5 business days.', severity: 'WARNING', raisedAt: '2025-01-24' }] },
  { id: '4', claimsiqId: 'CIQ-2025-00150', email: 'swilson@email.com', currentAddress: '321 Elm St', city: 'Phoenix', state: 'AZ', zip: '85001', moveInDate: 'Jul 2021', currentRent: '$1,400', occupation: 'Nurse', militaryService: 'No', claimType: 'RealPage', qualification: 'Not Qualified', defendant: 'N/A', status: 'Closed', lastUpdated: '2025-01-24' },
  { id: '5', claimsiqId: 'CIQ-2025-00151', email: 'djohnson@email.com', currentAddress: '555 Maple Dr', city: 'Chicago', state: 'IL', zip: '60601', moveInDate: 'Nov 2019', currentRent: '$2,350', occupation: 'Engineer', militaryService: 'No', claimType: 'RealPage', qualification: 'Qualified', defendant: 'Equity Residential', status: 'Under Review', lastUpdated: '2025-01-25' },
  { id: '6', claimsiqId: 'CIQ-2025-00152', email: 'alee@email.com', currentAddress: '901 Cedar Ln', city: 'Miami', state: 'FL', zip: '33101', moveInDate: 'Feb 2020', currentRent: '$1,950', occupation: 'Accountant', militaryService: 'No', claimType: 'RealPage', qualification: 'Qualified', defendant: 'MAA', status: 'New', lastUpdated: '2025-01-26' },
  { id: '7', claimsiqId: 'CIQ-2025-00153', email: 'rgarcia@email.com', currentAddress: '234 Birch Ave', city: 'Denver', state: 'CO', zip: '80201', moveInDate: 'Jun 2018', currentRent: '$1,750', occupation: 'Police Officer', militaryService: 'Yes', claimType: 'RealPage', qualification: 'Qualified', defendant: 'UDR Inc', status: 'Contacted', lastUpdated: '2025-01-24' },
  { id: '8', claimsiqId: 'CIQ-2025-00154', email: 'kpatel@email.com', currentAddress: '678 Walnut St', city: 'Seattle', state: 'WA', zip: '98101', moveInDate: 'Apr 2021', currentRent: '$2,500', occupation: 'Software Dev', militaryService: 'No', claimType: 'RealPage', qualification: 'Not Qualified', defendant: 'N/A', status: 'Closed', lastUpdated: '2025-01-23' },
];

const ResultsManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterQualification, setFilterQualification] = useState('all');
  const [filterMilitary, setFilterMilitary] = useState('all');
  const [sortField, setSortField] = useState('claimsiqId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailResult, setDetailResult] = useState<ClaimResult | null>(null);
  const [deficiencyResult, setDeficiencyResult] = useState<ClaimResult | null>(null);
  const [checklistResult, setChecklistResult] = useState<ClaimResult | null>(null);
  const perPage = 10;

  const filteredResults = useMemo(() => {
    return mockResults
      .filter((r) => {
        const s = searchTerm.toLowerCase();
        const matchSearch = !s || r.claimsiqId.toLowerCase().includes(s) ||
          r.currentAddress.toLowerCase().includes(s) || r.email.toLowerCase().includes(s) ||
          r.defendant.toLowerCase().includes(s) || r.occupation.toLowerCase().includes(s);
        const matchStatus = filterStatus === 'all' || r.status === filterStatus;
        const matchQual = filterQualification === 'all' || r.qualification === filterQualification;
        const matchMil = filterMilitary === 'all' || r.militaryService === filterMilitary;
        return matchSearch && matchStatus && matchQual && matchMil;
      })
      .sort((a: any, b: any) => {
        const av = a[sortField], bv = b[sortField];
        return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
      });
  }, [searchTerm, filterStatus, filterQualification, filterMilitary, sortField, sortDir]);

  const paginatedResults = filteredResults.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(filteredResults.length / perPage);

  const qualifiedCount = mockResults.filter((r) => r.qualification === 'Qualified').length;
  const veteranCount = mockResults.filter((r) => r.militaryService === 'Yes').length;

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelectedIds(selectedIds.length === filteredResults.length ? [] : filteredResults.map((r) => r.id));
  };

  const handleExport = (format: string) => {
    alert(`Exporting ${selectedIds.length || filteredResults.length} results as ${format.toUpperCase()}`);
  };

  const SortIcon: React.FC<{ field: string }> = ({ field }) => (
    sortField === field ? (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : null
  );

  return (
    <ContentWrapper>
      <PageHeader
        title="Results Management"
        subtitle="View, filter, and export your claims discovery results"
        breadcrumbs={[
          { label: 'Claims Discovery' },
          { label: 'Results Management', isActive: true },
        ]}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><FileText className="h-5 w-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{mockResults.length}</p>
              <p className="text-xs text-gray-500">Total Claims Found</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="h-5 w-5 text-green-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{qualifiedCount}</p>
              <p className="text-xs text-gray-500">Qualified ({((qualifiedCount / mockResults.length) * 100).toFixed(1)}%)</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg"><Shield className="h-5 w-5 text-purple-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{veteranCount}</p>
              <p className="text-xs text-gray-500">Military Service</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card className="bg-white border border-gray-200 shadow-sm mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder="Search by ClaimsIQ ID, address, email, or defendant..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-2" /> Filters
              <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
            <select onChange={(e) => e.target.value && handleExport(e.target.value)} className="bg-blue-600 text-white px-4 py-2 pr-8 rounded-lg hover:bg-blue-700 cursor-pointer text-sm" defaultValue="">
              <option value="" disabled>Export Results</option>
              <option value="excel">Export to Excel</option>
              <option value="csv">Export to CSV</option>
              <option value="json">Export to JSON</option>
            </select>
          </div>
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="all">All Statuses</option>
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualification</label>
                <select value={filterQualification} onChange={(e) => setFilterQualification(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="all">All</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Not Qualified">Not Qualified</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Military Service</label>
                <select value={filterMilitary} onChange={(e) => setFilterMilitary(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="all">All</option>
                  <option value="Yes">Veterans</option>
                  <option value="No">Non-Veterans</option>
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={() => { setSearchTerm(''); setFilterStatus('all'); setFilterQualification('all'); setFilterMilitary('all'); }} className="text-sm text-gray-600 hover:text-gray-800 underline">
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card className="bg-white border border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg text-gray-900">Claims Discovery Results</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Showing {paginatedResults.length} of {filteredResults.length} results
                {selectedIds.length > 0 && ` · ${selectedIds.length} selected`}
              </p>
            </div>
            {selectedIds.length > 0 && (
              <div className="flex gap-2">
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs">Export Selected</Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs">Generate Retainers</Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 w-10">
                    <input type="checkbox" checked={selectedIds.length === filteredResults.length && filteredResults.length > 0} onChange={selectAll} className="rounded border-gray-300" />
                  </th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase cursor-pointer" onClick={() => handleSort('claimsiqId')}>
                    <span className="flex items-center gap-1">ClaimsIQ ID# <SortIcon field="claimsiqId" /></span>
                  </th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Current Address</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Overlap Flags</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Qualification</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Defendant</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedResults.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleSelect(r.id)} className="rounded border-gray-300" />
                    </td>
                    <td className="p-4">
                      <span className="font-medium text-gray-900 text-sm">{r.claimsiqId}</span>
                      <br /><span className="text-xs text-gray-500">{r.email}</span>
                    </td>
                    <td className="p-4 text-sm text-gray-700">
                      {r.currentAddress}<br /><span className="text-xs text-gray-500">{r.city}, {r.state} {r.zip}</span>
                    </td>
                    <td className="p-4"><Badge variant="outline" className="text-xs">{r.claimType}</Badge></td>
                    <td className="p-4"><OverlapFlagsCell overlapFlags={(r as any).overlapFlags} deficiencies={(r as any).deficiencies} onDeficiencyClick={() => setDeficiencyResult(r)} /></td>
                    <td className="p-4">
                      <Badge variant={r.qualification === 'Qualified' ? 'success' : 'destructive'} className="text-xs">
                        {r.qualification}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm text-gray-700">{r.defendant}</td>
                    <td className="p-4">
                      <Badge className={`text-xs ${
                        r.status === 'New' ? 'bg-green-100 text-green-800' :
                        r.status === 'Contacted' ? 'bg-blue-100 text-blue-800' :
                        r.status === 'Under Review' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>{r.status}</Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <button onClick={() => (r as any).overlapFlags ? setChecklistResult(r) : setDetailResult(r)} className="p-1.5 hover:bg-gray-100 rounded" title="View Details"><Eye className="h-4 w-4 text-gray-500" /></button>
                        <button className="p-1.5 hover:bg-gray-100 rounded" title="Contact"><Mail className="h-4 w-4 text-gray-500" /></button>
                        <button className="p-1.5 hover:bg-gray-100 rounded" title="Generate Retainer"><FileText className="h-4 w-4 text-gray-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">Page {currentPage} of {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {detailResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetailResult(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{detailResult.claimsiqId}</h2>
              <button onClick={() => setDetailResult(null)} className="p-1 hover:bg-gray-100 rounded"><XCircle className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              {[
                { label: 'Email', value: detailResult.email },
                { label: 'Current Address', value: `${detailResult.currentAddress}, ${detailResult.city}, ${detailResult.state} ${detailResult.zip}` },
                { label: 'Move-in Date', value: detailResult.moveInDate },
                { label: 'Monthly Rent', value: detailResult.currentRent },
                { label: 'Occupation', value: detailResult.occupation },
                { label: 'Military Service', value: detailResult.militaryService },
                { label: 'Claim Type', value: detailResult.claimType },
                { label: 'Qualification', value: detailResult.qualification },
                { label: 'Defendant', value: detailResult.defendant },
                { label: 'Status', value: detailResult.status },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">{item.label}</span>
                  <span className="text-sm font-medium text-gray-900">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Contact Client</Button>
              <Button variant="outline" className="flex-1">Generate Retainer</Button>
            </div>
          </div>
        </div>
      )}

      {/* Deficiency Panel */}
      {deficiencyResult && (deficiencyResult as any).deficiencies && (
        <DeficiencyPanel
          claimsiqId={deficiencyResult.claimsiqId}
          deficiencies={(deficiencyResult as any).deficiencies}
          overlapFlags={(deficiencyResult as any).overlapFlags}
          onClose={() => setDeficiencyResult(null)}
        />
      )}

      {/* Qualification Checklist */}
      {checklistResult && (checklistResult as any).overlapFlags && (
        <QualificationChecklist
          claimsiqId={checklistResult.claimsiqId}
          claimantName={checklistResult.email}
          overlapFlags={(checklistResult as any).overlapFlags}
          deficiencies={(checklistResult as any).deficiencies}
          onClose={() => setChecklistResult(null)}
        />
      )}
    </ContentWrapper>
  );
};

export default ResultsManagement;